<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { useStringFunctions, useUtilities } from '@apparatix/composables';
import type { Content } from '@apparatix/types/AccountProfiles';
import { Stack } from '@apparatix/library/core';

type Props = {
  contents: Content<'composite-list'>;
}

type EditableModel = Array<{ key: string, items: string[] }>;

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'on:change', data: Content<'composite-list'>): void;
}>();

const { forKeys } = useUtilities();
const { isEmptyString } = useStringFunctions();

const model = ref<EditableModel>([]);

const onAddCategory = () => {
  if (model.value.some(section => isEmptyString(section.key, true) || section.key === 'Section name')) {
    return;
  }

  model.value.push({
    key: 'Section name',
    items: [''],
  });
};

const onAddItem = (sectionIndex: number) => {
  const itemsLength = model.value[sectionIndex].items.length;
  const lastItemEmpty = isEmptyString(model.value[sectionIndex].items[itemsLength - 1], true);
  if (!lastItemEmpty) {
    model.value[sectionIndex].items.push('');
  }
};

const onChange = () => {
  // Since objects cannot include duplicate or empty keys, we need to parse the model into a map
  // before converting the model array back to an object since the user may have entered duplicate or empty keys
  // We use Set<string> instead of string[] for the map value to easily remove duplicate items
  const itemsMap = new Map<string, Set<string>>();

  model.value.forEach(section => {
    if (!itemsMap.has(section.key)) {
      itemsMap.set(section.key, new Set(section.items.filter(item => !isEmptyString(item, true))));
    } else {
      const existingItems = Array.from(itemsMap.get(section.key));
      itemsMap.set(section.key, new Set([...existingItems, ...section.items].filter(item => !isEmptyString(item, true))));
    }
  });

  const returnObject: object = {};

  itemsMap.forEach((value, key) => {
    returnObject[key] = Array.from(value);
  });

  emit('on:change', returnObject as Content<'composite-list'>);
};

onMounted(() => {
  if (!props.contents || typeof props.contents !== 'object') {
    model.value = [
      { key: 'Title', items: ['']},
    ];
    return;
  }

  forKeys(props.contents, (key, items: string[]) => {
    model.value.push({ key, items });
  });

});
</script>
<template>
  <Stack>
    <Stack v-for="(section, idx) in model" :key="idx" :gap="2">
      <InputText
        v-model="section.key"
        class="w-1/2"
        variant="filled"
        size="small"
        @update:model-value="onChange"
      />
      <Stack :gap="2" class="ml-2 md:ml-4 lg:ml-6">
        <InputText
          v-for="(_, jdx) in section.items"
          :key="jdx"
          v-model="section.items[jdx]"
          size="small"
          class="w-1/2"
          @update:model-value="onChange"
        />
        <div>
          <Button
            label="Add Item"
            size="small"
            severity="secondary"
            outlined
            @click="onAddItem(idx)"
          />
        </div>
      </Stack>
    </Stack>
    <div>
      <Button
        label="Add Section"
        severity="secondary"
        size="small"
        outlined
        @click="onAddCategory"
      />
    </div>
  </Stack>
</template>
